import { useState } from 'react';
import axios from 'axios';
import SERVER from '../hooks/useServer';
import { useTranslation } from 'react-i18next';

const Contact = () => {
  const {t} = useTranslation();
  const [phoneNumber, setPhoneNumber] = useState('');
  const [countryCode, setCountryCode] = useState('+33');
  const [selectedCountry, setSelectedCountry] = useState('🇫🇷');
  const [showDropdown, setShowDropdown] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const countries = [
    { flag: '🇫🇷', code: '+33', name: 'France' },
    { flag: '🇩🇪', code: '+49', name: 'Germany' },
    { flag: '🇪🇸', code: '+34', name: 'Spain' },
    { flag: '🇮🇹', code: '+39', name: 'Italy' },
    { flag: '🇬🇧', code: '+44', name: 'United Kingdom' },
    { flag: '🇺🇸', code: '+1', name: 'United States' },
    { flag: '🇨🇦', code: '+1', name: 'Canada' },
  ];

  const handleCountrySelect = (country) => {
    setSelectedCountry(country.flag);
    setCountryCode(country.code);
    setShowDropdown(false);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const response = await axios.post(`${SERVER}/contact`, {
        name: formData.name,
        email: formData.email,
        phone: `${countryCode} ${phoneNumber}`,
        message: formData.message
      });

      setSubmitStatus({ 
        success: true, 
        message: t('contact.success')
      });
      // Reset form
      setFormData({
        name: '',
        email: '',
        message: ''
      });
      setPhoneNumber('');
    } catch (error) {
      setSubmitStatus({ 
        success: false, 
        message: t('contact.error')
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="w-full max-w-4xl mx-auto px-4 pt-14 pb-16">
      {/* Contact Card */}
      <div className=" rounded-2xl shadow-lg p-8 md:p-12">
        {/* Title */}
        <h1 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-8">
          {t('contact.title')}
        </h1>

        {/* Status Message */}
        {submitStatus && (
          <div className={`w-full max-w-2xl mx-auto mb-6 p-4 rounded-lg flex items-center gap-3 ${
            submitStatus.success 
              ? 'bg-green-50 text-green-700 border border-green-200' 
              : 'bg-red-50 text-red-700 border border-red-200'
          }`}>
            <div className="text-xl font-bold">
              {submitStatus.success ? '✓' : '✕'}
            </div>
            <div className="flex-1 text-base">
              {submitStatus.message}
            </div>
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="w-full max-w-2xl mx-auto space-y-6">
          {/* Name Input */}
          <div className="w-full">
            <input 
              type="text" 
              name="name"
              placeholder={t('contact.name')}
              value={formData.name}
              onChange={handleChange}
              required
              className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg text-base focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all"
            />
          </div>

          {/* Email Input */}
          <div className="w-full">
            <input 
              type="email" 
              name="email"
              placeholder={t('contact.email')}
              value={formData.email}
              onChange={handleChange}
              required
              className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg text-base focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all"
            />
          </div>

          {/* Phone Input */}
          <div className="w-full grid grid-cols-[auto_1fr] gap-3">
            {/* Country Code Selector */}
            <div className="relative">
              <button
                type="button"
                onClick={() => setShowDropdown(!showDropdown)}
                className="bg-gray-50 border border-gray-200 rounded-lg px-4 py-3 flex items-center gap-2 hover:bg-gray-100 transition-colors"
              >
                <span className="text-2xl">{selectedCountry}</span>
                <span className="text-gray-600 font-medium">{countryCode}</span>
                <svg 
                  className={`w-4 h-4 text-gray-400 transition-transform ${showDropdown ? 'rotate-180' : ''}`}
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              
              {/* Dropdown */}
              {showDropdown && (
                <div className="absolute top-full left-0 mt-2 bg-white border border-gray-200 rounded-lg shadow-lg z-10 min-w-[200px] max-h-[300px] overflow-y-auto">
                  {countries.map((country, index) => (
                    <button
                      key={index}
                      type="button"
                      onClick={() => handleCountrySelect(country)}
                      className="w-full px-4 py-2 flex items-center gap-3 hover:bg-gray-50 transition-colors text-left"
                    >
                      <span className="text-2xl">{country.flag}</span>
                      <span className="text-gray-700 font-medium">{country.code}</span>
                      <span className="text-gray-500 text-sm">{country.name}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            
            {/* Phone Number Input */}
            <input 
              type="tel"
              placeholder={t('contact.phone')}
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value)}
              className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg text-base focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all"
            />
          </div>

          {/* Message Textarea */}
          <div className="w-full">
            <textarea 
              name="message"
              placeholder={t('contact.message')}
              value={formData.message}
              onChange={handleChange}
              required
              rows="6"
              className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg text-base resize-y min-h-[150px] max-h-[300px] focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all"
            />
          </div>

          {/* Submit Button */}
          <button 
            type="submit"
            disabled={isSubmitting}
            className="w-full bg-primary hover:bg-blue-700 text-white font-medium text-base py-3 px-6 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? t('contact.sending') : t('contact.send')}
          </button>
        </form>
      </div>
    </div>
  );
}

export default Contact;